import {NavButtonProps} from 'Event/Dashboard/components/NavButton'
import {Sponsor} from 'Event/Sponsors'
import {Template} from 'Event/template'
import {TemplateSave, useSaveTemplate} from 'Event/TemplateUpdateProvider'
import {useTemplate} from 'Event/TemplateProvider'
import {generateHashId} from 'lib/crypto/hash'
import {createPositions, orderedIdsByPosition} from 'lib/list'
import React from 'react'
import {useAddTranslation, useTranslationLanguage} from 'Event/LanguageProvider'

export function useDuplicateSponsor() {
  const template = useTemplate()
  const saveTemplate = useSaveTemplate()
  const addTranslation = useAddTranslation()
  const targetLanguage = useTranslationLanguage()

  return React.useCallback(
    (copyId: string) =>
      saveDuplicatedSponsor(
        copyId,
        template,
        saveTemplate,
        addTranslation,
        targetLanguage,
      ),
    [addTranslation, saveTemplate, targetLanguage, template],
  )
}

async function saveDuplicatedSponsor(
  sourceId: string,
  template: Template,
  saveTemplate: TemplateSave<Template>,
  addTranslation: ReturnType<typeof useAddTranslation>,
  targetLanguage: string,
) {
  const languageTranslations =
    template.localization?.translations?.[targetLanguage] ?? {}

  const items = template.sponsors.items
  const source = items[sourceId]
  if (!source) {
    return
  }

  const sponsorHash = await generateHashId([
    'sponsor_',
    Date.now().toString(),
    Math.random().toString(),
  ])
  const newId = `sponsor_${sponsorHash}`

  const nextPosition =
    Math.max(0, ...Object.values(items).map((b) => b.position ?? 0)) + 1

  const {newButtons, buttonTranslations} = await duplicateButtons(
    source,
    languageTranslations,
  )

  const newSponsor = {
    ...source,
    position: nextPosition,
    name: `{{${newId}_name}}`,
    description: `{{${newId}_description}}`,
    buttons: newButtons,
  }

  const name = languageTranslations[`${sourceId}_name`] ?? ''
  const description = languageTranslations[`${sourceId}_description`] ?? ''

  saveTemplate({
    sponsors: {
      items: {
        [newId]: newSponsor,
      },
    },
    localization: addTranslation({
      [`${newId}_name`]: name,
      [`${newId}_description`]: description,
      ...buttonTranslations,
    }),
  })
}

async function duplicateButtons(
  source: Sponsor,
  languageTranslations: Record<string, string>,
) {
  const buttons = source.buttons || {}
  const positions = createPositions(orderedIdsByPosition(buttons))
  const newButtons: Record<string, NavButtonProps> = {}
  const buttonTranslations: Record<string, string> = {}

  for (const buttonId of Object.keys(buttons)) {
    const newButtonId = await generateHashId([
      'sponsor_button_',
      buttonId,
      Date.now().toString(),
      Math.random().toString(),
    ])

    newButtons[newButtonId] = {
      ...buttons[buttonId],
      text: `{{sponsor_button_${newButtonId}_text}}`,
      position: positions[buttonId].position,
    }

    buttonTranslations[`sponsor_button_${newButtonId}_text`] =
      languageTranslations[`sponsor_button_${buttonId}_text`] ?? ''
  }

  return {newButtons, buttonTranslations}
}
